<script lang="ts">
  import {
    DataTable,
    DataTableHead,
    DataTableRow,
    DataTableCell,
    DataTableBody,
  } from 'svelte-materialify';
  import { onMount, createEventDispatcher } from 'svelte';
  import { UserService } from '../../modules';
  import { link } from 'svelte-spa-router';
  import dayjs from 'dayjs';
  import { AccountType } from '../../modules/user/user.interface';
  import type { User } from '../../modules/user/user.interface';
  import Scroller from '../Scroller.svelte';
  import Lazy from 'svelte-lazy';
  import Select from '../Select.svelte';
  import { capitalize } from 'lodash';

  export let project: string;
  const dispatch = createEventDispatcher();

  onMount(() => fetchUsers(true));

  const allTypes: AccountType[] = Object.values(AccountType);

  let users: User[] = [];
  let page = 1;
  let limit = 50;
  let inputText = '';
  let disableLazy = true;
  let selectedType = allTypes[0];

  $: onSelectedTypesChange(selectedType);

  async function fetchUsers(reset?: boolean) {
    if (reset) {
      page = 1;
      users = [];
    }

    try {
      const result = await UserService.getUserList({
        project,
        queryKey: inputText,
        page,
        limit,
        accountType: selectedType,
      });

      users = [...users, ...result.docs];

      dispatch('user-count', users.length);

      if (result.nextPage) {
        disableLazy = false;
      }
      if (!result.docs?.length) {
        return;
      }
    } catch (error) {}
  }

  function onScrollEnded() {
    page++;
    fetchUsers();
    disableLazy = true;
  }

  async function onSelectedTypesChange(_selectedType: string) {
    fetchUsers(true);
  }

  function checkAccountType(date: string) {
    const today = dayjs();
    const expireDateObj = dayjs(date, { format: 'YYYY-MM-DD' });
    let accountType = AccountType.FREE;

    if (expireDateObj.isAfter(today)) {
      accountType = AccountType.PRO_MONTHLY;
    }

    return accountType;
  }

  async function updateUserMembershipDate(e, user: User) {
    const { target } = e;
    const accountType = checkAccountType(target.value);
    if (target) {
      await UserService.updateUserAccount(user.id, target.value, accountType);
      fetchUsers(true);
    }
  }
</script>

<div class="filters-container">
  <input
    bind:value={inputText}
    placeholder="Enter user e-mail"
    on:input={() => {
      fetchUsers(true);
    }}
  />
  <div class="select">
    <Select items={allTypes} bind:selectedItem={selectedType}>Types</Select>
  </div>
</div>
<Scroller>
  <DataTable>
    <DataTableHead>
      <DataTableRow>
        <DataTableCell>Name</DataTableCell>
        <DataTableCell>Email</DataTableCell>
        <DataTableCell>Date Registered</DataTableCell>
        <DataTableCell>Expiration Date</DataTableCell>
        <DataTableCell>Account Type</DataTableCell>
        <DataTableCell>Role</DataTableCell>
      </DataTableRow>
    </DataTableHead>
    <DataTableBody>
      {#each users as user}
        <DataTableRow>
          <DataTableCell>
            <a href="/project/{project}/user/{user.id}" use:link>
              {user.username}
            </a>
          </DataTableCell>
          <DataTableCell>{user.email}</DataTableCell>
          <DataTableCell>{dayjs(user.createdAt).format('DD-MM-YYYY')}</DataTableCell>
          <DataTableCell>
            <input
              type="date"
              class="expiration-date"
              min={dayjs().format('YYYY-MM-DD')}
              bind:value={user.proExpireDate}
              on:change={(e) => updateUserMembershipDate(e, user)}
            />
          </DataTableCell>
          <DataTableCell>
            <div class="account-type">
              {#if user?.accountType?.includes(AccountType.PRO)}
                <span class="toggle toggle-pro"
                  >{capitalize(user?.accountType?.replace('_', ' '))}</span
                >
              {:else}
                <span class="toggle toggle-free">Free</span>
              {/if}
            </div></DataTableCell
          >
          <DataTableCell>{user.role}</DataTableCell>
        </DataTableRow>
      {/each}
      {#if !disableLazy}
        <Lazy offset={500} onload={onScrollEnded}>Loading...</Lazy>
      {/if}
    </DataTableBody>
  </DataTable>
</Scroller>

<style lang="scss">
  .filters-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    input {
      width: 30%;
      padding: 8px;
      font-size: 16px;
      border: 1px solid #ccc;
      border-radius: 4px;
      margin: 16px 0;
    }
    .select {
      margin-top: 12px;
    }
  }
  .account-type {
    display: flex;
    cursor: pointer;
  }

  .toggle {
    padding: 5px 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    margin: 0 5px;
    color: #fff;
    cursor: pointer;
  }
  .expiration-date {
    width: 130px;
  }

  .toggle-free {
    background-color: #000;
  }

  .toggle-pro {
    padding: 5px 14px;
    background-color: #00aa00;
  }
</style>
